import React from 'react';
import { Box } from '@mui/material';

export const sectionColorsDark = ['#60a5fa', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#22d3ee', '#f472b6'];
export const sectionColorsLight = ['#1d4ed8', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#db2777'];
export const sectionIcons = ['🎯', '🔬', '⚡', '🛡️', '🧩', '🔑', '💎'];

export const renderInlineContent = (text, color, isLight) => {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\$[^$]+\$)/g;
  let last = 0, m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const raw = m[0];
    if (raw.startsWith('`') || raw.startsWith('$')) {
      const content = raw.startsWith('`') ? raw.slice(1, -1) : raw.slice(1, -1);
      parts.push(
        <Box key={m.index} component="code" sx={{
          bgcolor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
          color: isLight ? '#1e293b' : '#3b82f6',
          px: 0.8, py: 0.2, borderRadius: 1,
          fontFamily: 'monospace', fontSize: '0.85em', fontWeight: 700,
          border: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
          mx: 0.2
        }}>
          {content}
        </Box>
      );
    } else if (raw.startsWith('**')) {
      parts.push(<strong key={m.index} style={{ color: isLight ? '#0f172a' : '#f8fafc', fontWeight: 800 }}>{raw.slice(2, -2)}</strong>);
    } else if (raw.startsWith('*')) {
      parts.push(<em key={m.index} style={{ color: isLight ? '#0f172a' : '#f8fafc', fontStyle: 'italic', fontWeight: 600 }}>{raw.slice(1, -1)}</em>);
    }
    last = m.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

export const parseMarkdownToBlocks = (lines) => {
  const blocks = []; let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    // Q&A detection
    if (line.match(/^### Q\s*:/i) || line.match(/^#### Q\s*:/i)) {
      const question = line.replace(/^###?\s*Q\s*:\s*/i, '').trim();
      let answer = ''; i++;
      while (i < lines.length && !lines[i].trim().startsWith('##') && !lines[i].trim().startsWith('```')) {
        const aLine = lines[i].trim();
        if (aLine.match(/^\*\*A\s*:/i)) {
          answer = aLine.replace(/^\*\*A\s*:\s*\*\*/i, '').replace(/^\*\*A\s*:\s*/i, '').trim();
        } else if (answer && aLine) {
          answer += ' ' + aLine;
        }
        i++;
      }
      if (question && answer) blocks.push({ type: 'qa', question, answer });
      continue;
    }

    // Code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      let closingIdx = -1;
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim().startsWith('```')) {
          closingIdx = j;
          break;
        }
      }

      if (closingIdx !== -1) {
        const code = [];
        for (let j = i + 1; j < closingIdx; j++) {
          code.push(lines[j]);
        }
        blocks.push({ type: 'code', lang, content: code.join('\n') });
        i = closingIdx + 1;
        continue;
      }
    }

    // Grouped Callout / GitHub Alert detection
    if (line.startsWith('>')) {
      const calloutLines = [];
      let alertType = null;
      
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        let content = lines[i].trim().slice(1).trim();
        
        // Detect GitHub-style alerts: [!TIP], [!NOTE], etc.
        const alertMatch = content.match(/^\[!(TIP|NOTE|IMPORTANT|WARNING|CAUTION)\]/i);
        if (alertMatch) {
          alertType = alertMatch[1].toUpperCase();
          content = content.replace(/^\[!(TIP|NOTE|IMPORTANT|WARNING|CAUTION)\]/i, '').trim();
        }
        
        if (content) calloutLines.push(content);
        i++;
      }
      
      if (calloutLines.length > 0) {
        blocks.push({ 
          type: 'callout', 
          text: calloutLines.join(' '), 
          alertType 
        });
      } else if (alertType) {
        // Handle case where only [!TIP] exists on a line
        blocks.push({ type: 'callout', text: '', alertType });
      }
      continue;
    }

    // List items
    if (line.match(/^[*-]\s/)) {
      const items = [];
      while (i < lines.length && lines[i].trim().match(/^[*-]\s/)) {
        items.push(lines[i].trim().slice(2).trim());
        i++;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    // Sub-heading detection (### or ####)
    if (line.startsWith('###')) {
      const level = line.match(/^#+/)[0].length;
      blocks.push({ type: 'heading', level, text: line.replace(/^#+\s*/, '').trim() });
      i++; continue;
    }

    // Horizontal divider detection
    if (line.match(/^---$/) || line.match(/^___$/)) {
      blocks.push({ type: 'divider' });
      i++; continue;
    }

    // Table detection (lines starting with |)
    if (line.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      if (tableLines.length > 0) {
        blocks.push({ type: 'table', content: tableLines.join('\n') });
        continue;
      }
    }

    // QAHoverCard Tag detection (JSX-like syntax)
    if (line.startsWith('<QAHoverCard')) {
      const tagContent = [];
      let j = i;
      while (j < lines.length) {
        tagContent.push(lines[j].trim());
        if (lines[j].includes('/>')) break;
        j++;
      }
      
      const fullTag = tagContent.join(' ');
      // Refined attribute extraction using backreferences for quotes
      const questionMatch = fullTag.match(/question=(["'])(.*?)\1/);
      const answerMatch = fullTag.match(/answer=(["'])(.*?)\1/);
      
      if (questionMatch && answerMatch) {
        blocks.push({ 
          type: 'qa', 
          question: questionMatch[2], 
          answer: answerMatch[2] 
        });
        i = j + 1;
        continue;
      }
    }

    blocks.push({ type: 'text', text: line });
    i++;
  }
  return blocks;
};

export const sanitize = (text) => {
  if (!text) return '';
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\$/g, '')
    .replace(/Difficulty:\s*/gi, '')
    .replace(/Pattern:\s*/gi, '')
    .replace(/^[ \t·|*-]+/gm, '• ') // Transform multi-line bullets (excluding '>')
    .trim();
};
